/**
 * @fileoverview Unified sales-roster query hook.
 * @description Wraps GET /sales/sales-users in a TanStack query. Used wherever an id needs
 * resolving to a display name (tenant owner, activity author, ownership-transfer actor) or a
 * viewer's own sales-roster record needs finding by email.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getSalesRoster } from '@/features/sales/services/sales-roster.service'
import { SALES_ROSTER_QUERY_KEY } from '@/shared/constants/sales-query-keys.constants'

import type { SalesRosterFilters } from '@/features/sales/services/sales-roster.service'

export { SALES_ROSTER_QUERY_KEY }

/**
 * @description Fetches the sales roster matching the supplied filters.
 * @param {SalesRosterFilters} [filters] - The filters to apply.
 * @param {{ enabled?: boolean }} [options] - `enabled: false` skips the request.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, error, ...).
 */
export function useSalesRoster(
  filters: SalesRosterFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...SALES_ROSTER_QUERY_KEY, filters],
    queryFn: () => getSalesRoster(filters),
    enabled: options.enabled ?? true,
  })
}
